import { UPGRADE_OFFER_FEATURE } from '@pubstack/common/src/upgrade'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScope } from './useScope'

type StaffAccessScope = { hasFeature: (feature: never) => boolean; onboardedFeatures?: readonly string[] }
type StaffAccessViewer = { isAdmin: boolean }

/**
 * The state of a product for the person looking, given the scope's two toggles: `onboarded` and `user access`.
 *
 * - access on: an ordinary scope, the same for everyone.
 * - onboarded, access off: `isStaffOnly`. Pubstack admins work in it as on any scope that has the product, marked with a
 *   shield; everybody else finds it not available.
 * - not onboarded: off for everybody.
 *
 * Pure, so non-React code can ask it too.
 */
export const staffAccessState = (scope: StaffAccessScope, user: StaffAccessViewer, feature: string, onboarded: string = feature) => {
  const hasAccess = (scope.hasFeature as (feature: string) => boolean)(feature)
  const isStaffOnly = !hasAccess && (scope.onboardedFeatures?.includes(onboarded) ?? false)
  const isStaffOnProduct = isStaffOnly && user.isAdmin
  return { hasAccess, has: hasAccess || isStaffOnProduct, isStaffOnly, isStaffOnProduct, isLockedToStaff: isStaffOnly && !user.isAdmin }
}

/** The same question for the raw scope (no `hasFeature` on it): its access features are the ones that grant. */
export const nodeStaffStateOfScope = (scope: { accessFeatures?: readonly string[]; onboardedFeatures?: readonly string[] }, user: StaffAccessViewer) =>
  nodeStaffState({ hasFeature: ((feature: string) => scope.accessFeatures?.includes(feature) ?? false) as (feature: never) => boolean, onboardedFeatures: scope.onboardedFeatures }, user)

/** Node (`pbsStandalone`) for the person looking, see {@link staffAccessState}. */
export const nodeStaffState = (scope: StaffAccessScope, user: StaffAccessViewer) => {
  const { hasAccess, has, isStaffOnly, isStaffOnProduct, isLockedToStaff } = staffAccessState(scope, user, UPGRADE_OFFER_FEATURE.node)
  return { hasNodeAccess: hasAccess, hasNode: has, isStaffOnly, isStaffOnNode: isStaffOnProduct, isLockedToStaff }
}

/**
 * Node for the person looking: the scope's `pbsStandalone` access, or staff on a scope onboarded on Node
 * whose users have not been given access yet.
 *
 * `isStaffOnly` is that in-between state. Pubstack admins work in it as on any Node scope; everybody else
 * finds Node not available. Once user access is on, it is an ordinary Node scope and none of this applies.
 */
export const useNodeStaffAccess = () => nodeStaffState(useScope(), useAuthenticatedUser())
